// Excel export for the Building Index. Kept out of BuildingIndex.js so the
// ExcelJS bundle only loads when somebody actually asks for a file.

const NAVY = 'FF1F3A68';
const INK = 'FF15202E';
const GREY = 'FF6B7280';
const RULE = 'FFE6E8EC';
const BLANK_SCORE = 'FFEDEFF2';

// Anything larger and Excel spends longer drawing logos than the file is worth.
const LOGO_ROW_LIMIT = 2000;
const ROW_HEIGHT = 24;
const LOGO_BOX = { width: 76, height: 20 };

const COLUMNS = [
  { header: '', key: 'logo', width: 13 },
  { header: 'Management', key: 'company', width: 26 },
  { header: 'Corp #', key: 'corp', width: 11 },
  { header: 'Address', key: 'address', width: 34 },
  { header: 'City', key: 'city', width: 15 },
  { header: 'Overall', key: 'overall', width: 9, score: true },
  { header: 'Warmth', key: 'warmth', width: 9, score: true },
  { header: 'Fit', key: 'fit', width: 9, score: true },
  { header: 'Manager(s)', key: 'managers', width: 26 },
  { header: 'Age', key: 'age', width: 7, number: true },
  { header: 'Suites', key: 'units', width: 8, number: '#,##0' },
  { header: 'Board members', key: 'board', width: 48 },
  { header: 'Hudson status', key: 'status', width: 14 },
  { header: 'Connection', key: 'connection', width: 22 },
  { header: 'Km from office', key: 'km', width: 13, number: '0.0' },
  { header: 'Why it is warm', key: 'warmthWhy', width: 52 },
  { header: 'Why it fits', key: 'fitWhy', width: 52 },
  { header: 'Registry updated', key: 'updated', width: 15 },
  { header: 'Condo registry', key: 'registry', width: 16 },
];

function hex(rgb) {
  return `FF${rgb.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`.toUpperCase();
}

function extensionOf(src) {
  const match = /\.(png|jpe?g|gif)(\?|$)/i.exec(src || '');
  if (!match) return '';
  const raw = match[1].toLowerCase();
  return raw === 'jpg' ? 'jpeg' : raw;
}

function toBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 8192) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 8192));
  }
  return window.btoa(binary);
}

// One fetch per distinct logo, whatever the row count. The natural size comes
// along so wide and square logos both keep their shape in the sheet.
async function loadLogos(rows) {
  const sources = [...new Set(rows.map((row) => row.logo).filter((src) => src && extensionOf(src)))];
  const entries = await Promise.all(sources.map(async (src) => {
    try {
      const response = await fetch(src);
      if (!response.ok) return null;
      const buffer = await response.arrayBuffer();
      let ratio = LOGO_BOX.width / LOGO_BOX.height;
      try {
        const bitmap = await createImageBitmap(new Blob([buffer]));
        ratio = bitmap.width / bitmap.height;
        bitmap.close();
      } catch (error) {
        // Undecodable image: fall back to filling the box.
      }
      const height = Math.min(LOGO_BOX.height, LOGO_BOX.width / ratio);
      return [src, {
        base64: toBase64(buffer),
        extension: extensionOf(src),
        width: Math.round(height * ratio),
        height: Math.round(height),
      }];
    } catch (error) {
      return null;
    }
  }));
  return new Map(entries.filter(Boolean));
}

function logoAnchor(logo, rowNumber) {
  const top = (ROW_HEIGHT - logo.height) / 2 / ROW_HEIGHT;
  return {
    tl: { col: 0.08, row: rowNumber - 1 + top },
    ext: { width: logo.width, height: logo.height },
    editAs: 'oneCell',
  };
}

function titleRows(sheet, span, { heading, summary }) {
  sheet.mergeCells(1, 1, 1, span);
  const title = sheet.getCell(1, 1);
  title.value = heading;
  title.font = { name: 'Calibri', size: 16, bold: true, color: { argb: NAVY } };
  title.alignment = { vertical: 'middle' };
  sheet.getRow(1).height = 26;

  sheet.mergeCells(2, 1, 2, span);
  const note = sheet.getCell(2, 1);
  note.value = summary;
  note.font = { name: 'Calibri', size: 10, color: { argb: GREY } };
  note.alignment = { vertical: 'middle' };
  sheet.getRow(2).height = 18;
}

function headerRow(sheet, rowNumber, columns) {
  const row = sheet.getRow(rowNumber);
  columns.forEach((column, index) => {
    const cell = row.getCell(index + 1);
    cell.value = column.header;
    cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
    cell.alignment = {
      vertical: 'middle',
      horizontal: column.score || column.number ? 'center' : 'left',
    };
  });
  row.height = 22;
  return row;
}

function buildingsSheet(workbook, rows, options) {
  const sheet = workbook.addWorksheet('Buildings', {
    views: [{ state: 'frozen', xSplit: 4, ySplit: 4 }],
    properties: { defaultRowHeight: 20 },
  });

  sheet.columns = COLUMNS.map((column) => ({ key: column.key, width: column.width }));
  titleRows(sheet, COLUMNS.length, options);
  headerRow(sheet, 4, COLUMNS);

  const withLogos = rows.length <= LOGO_ROW_LIMIT;

  rows.forEach((row, index) => {
    const rowNumber = index + 5;
    const sheetRow = sheet.getRow(rowNumber);
    sheetRow.height = ROW_HEIGHT;

    const tint = row.status === 'Active contract'
      ? 'FFEAF6EE'
      : row.status === 'Lost'
        ? 'FFFCECEA'
        : index % 2
          ? 'FFF7F8FA'
          : null;

    COLUMNS.forEach((column, columnIndex) => {
      const cell = sheetRow.getCell(columnIndex + 1);
      const value = row[column.key];

      if (column.key === 'logo') {
        // The picture sits over this cell, so the path itself stays out of the sheet.
      } else if (column.key === 'registry') {
        if (value) {
          cell.value = { text: 'Open registry', hyperlink: value };
          cell.font = { name: 'Calibri', size: 10, color: { argb: NAVY }, underline: true };
        }
      } else if (column.score) {
        cell.value = value == null ? null : value;
        cell.numFmt = '0.0';
        cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: INK } };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: value == null ? BLANK_SCORE : hex(options.scoreRgb(value)) },
        };
      } else {
        cell.value = value == null || value === '' ? null : value;
        cell.font = { name: 'Calibri', size: 10, color: { argb: INK } };
        if (typeof column.number === 'string') cell.numFmt = column.number;
      }

      if (!column.score) {
        if (tint) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tint } };
        if (column.key === 'company') cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: INK } };
        if (column.key === 'status' && row.status) {
          cell.font = {
            name: 'Calibri',
            size: 10,
            bold: true,
            color: { argb: row.status === 'Lost' ? 'FFB3261E' : 'FF1E6E3C' },
          };
        }
      }

      cell.alignment = {
        vertical: 'middle',
        horizontal: column.score || column.number ? 'center' : 'left',
      };
      cell.border = { bottom: { style: 'thin', color: { argb: RULE } } };
    });

    if (withLogos) options.placeLogo(sheet, row.logo, rowNumber);
  });

  sheet.autoFilter = {
    from: { row: 4, column: 1 },
    to: { row: Math.max(5, rows.length + 4), column: COLUMNS.length },
  };
  return sheet;
}

const COMPANY_COLUMNS = [
  { header: '', key: 'logo', width: 13 },
  { header: 'Management company', key: 'company', width: 34 },
  { header: 'Connection', key: 'connection', width: 22 },
  { header: 'Buildings', key: 'count', width: 11, number: '#,##0' },
  { header: 'Suites', key: 'units', width: 11, number: '#,##0' },
  { header: 'Avg overall', key: 'overall', width: 12, score: true },
  { header: 'Avg warmth', key: 'warmth', width: 12, score: true },
  { header: 'Avg fit', key: 'fit', width: 12, score: true },
  { header: 'Hudson buildings', key: 'clients', width: 17, number: '#,##0' },
];

function summarise(rows) {
  const groups = new Map();
  rows.forEach((row) => {
    let group = groups.get(row.company);
    if (!group) {
      group = {
        company: row.company,
        logo: row.logo,
        connection: row.connection,
        count: 0,
        units: 0,
        clients: 0,
        totals: { overall: 0, warmth: 0, fit: 0 },
        counts: { overall: 0, warmth: 0, fit: 0 },
      };
      groups.set(row.company, group);
    }
    group.count += 1;
    group.units += row.units || 0;
    if (row.status === 'Active contract') group.clients += 1;
    ['overall', 'warmth', 'fit'].forEach((key) => {
      if (row[key] != null) {
        group.totals[key] += row[key];
        group.counts[key] += 1;
      }
    });
  });

  const average = (total, count) => (count ? Math.round((total / count) * 10) / 10 : null);
  return [...groups.values()]
    .map((group) => ({
      ...group,
      overall: average(group.totals.overall, group.counts.overall),
      warmth: average(group.totals.warmth, group.counts.warmth),
      fit: average(group.totals.fit, group.counts.fit),
    }))
    .sort((a, b) => b.count - a.count || a.company.localeCompare(b.company));
}

function companiesSheet(workbook, rows, options) {
  const sheet = workbook.addWorksheet('Management', {
    views: [{ state: 'frozen', ySplit: 4 }],
  });
  sheet.columns = COMPANY_COLUMNS.map((column) => ({ key: column.key, width: column.width }));
  titleRows(sheet, COMPANY_COLUMNS.length, {
    heading: 'Management companies',
    summary: 'Averages cover only the buildings in this export.',
  });
  headerRow(sheet, 4, COMPANY_COLUMNS);

  summarise(rows).forEach((group, index) => {
    const rowNumber = index + 5;
    const sheetRow = sheet.getRow(rowNumber);
    sheetRow.height = ROW_HEIGHT;

    COMPANY_COLUMNS.forEach((column, columnIndex) => {
      const cell = sheetRow.getCell(columnIndex + 1);
      const value = group[column.key];
      if (column.score) {
        cell.value = value == null ? null : value;
        cell.numFmt = '0.0';
        cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: INK } };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: value == null ? BLANK_SCORE : hex(options.scoreRgb(value)) },
        };
      } else if (column.key !== 'logo') {
        cell.value = value == null || value === '' ? null : value;
        cell.font = { name: 'Calibri', size: 10, bold: column.key === 'company', color: { argb: INK } };
        if (typeof column.number === 'string') cell.numFmt = column.number;
      }
      cell.alignment = {
        vertical: 'middle',
        horizontal: column.score || column.number ? 'center' : 'left',
      };
      cell.border = { bottom: { style: 'thin', color: { argb: RULE } } };
    });

    options.placeLogo(sheet, group.logo, rowNumber);
  });
}

export default async function exportBuildingsToExcel({ rows, heading, summary, fileName, scoreRgb }) {
  const module = await import('exceljs');
  const ExcelJS = module.default || module;
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Hudson Condominium Solutions';
  workbook.created = new Date();

  // Each logo is embedded once and reused by both sheets.
  const logos = await loadLogos(rows);
  const imageIds = new Map();
  const placeLogo = (sheet, src, rowNumber) => {
    const logo = src && logos.get(src);
    if (!logo) return;
    if (!imageIds.has(src)) {
      imageIds.set(src, workbook.addImage({ base64: logo.base64, extension: logo.extension }));
    }
    sheet.addImage(imageIds.get(src), logoAnchor(logo, rowNumber));
  };

  buildingsSheet(workbook, rows, { heading, summary, scoreRgb, placeLogo });
  companiesSheet(workbook, rows, { scoreRgb, placeLogo });

  const buffer = await workbook.xlsx.writeBuffer();
  const url = URL.createObjectURL(new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
